import React, { useState, useRef } from 'react';
import { Box, Typography, TextField, Chip } from '@mui/material';

export default function ClinicTagChipField({
  label,
  required,
  value,
  onChange,
  helperText,
  placeholder,
  inputName = 'tag-chips',
  formatTag,
}) {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);

  const addTag = (raw) => {
    const cleaned = String(raw).replace(/,/g, '').trim();
    if (!cleaned) {
      setInput('');
      return;
    }
    const t = formatTag ? formatTag(cleaned) : cleaned;
    if (!t) {
      setInput('');
      return;
    }
    const dup = value.some((x) => String(x).toLowerCase() === t.toLowerCase());
    if (dup) {
      setInput('');
      return;
    }
    onChange([...value, t]);
    setInput('');
  };

  return (
    <Box sx={{ mt: 2, mb: 0.5 }}>
      <Typography variant="body2" component="label" sx={{ mb: 0.75, display: 'block', color: 'text.secondary' }}>
        {label}
        {required ? ' *' : ''}
      </Typography>
      <Box
        onMouseDown={(e) => {
          // Clicking anywhere in the field (padding, gaps between chips) focuses
          // the text input, so it never feels like a read-only box.
          if (e.target === e.currentTarget) {
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          py: 0.75,
          minHeight: 52,
          cursor: 'text',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          // White background (not the grey action.hover, which read as disabled).
          bgcolor: 'background.paper',
          transition: 'border-color 0.15s',
          '&:focus-within': {
            borderColor: 'primary.main',
          },
        }}
      >
        {value.map((tag) => (
          <Chip
            key={tag}
            size="small"
            label={tag}
            onDelete={() => onChange(value.filter((x) => x !== tag))}
            sx={{ fontWeight: 500 }}
          />
        ))}
        <TextField
          variant="standard"
          inputRef={inputRef}
          placeholder={value.length ? placeholder : placeholder || 'Type and press Enter'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addTag(input);
            } else if (e.key === 'Backspace' && !input && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => {
            if (input.trim()) addTag(input);
          }}
          inputProps={{
            name: inputName,
            // 'off' (not 'new-password') — 'new-password' makes browsers/password
            // managers pop a "suggest password" overlay over this tag field that
            // can intercept clicks and typing, making it feel read-only.
            autoComplete: 'off',
          }}
          sx={{
            flex: 1,
            minWidth: 140,
            '& .MuiInput-root': { marginTop: 0 },
            '& .MuiInput-underline:before, & .MuiInput-underline:after': { border: 'none' },
            '& .MuiInputBase-input': { py: 0.5 },
          }}
        />
      </Box>
      {helperText ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}
